/**
 * PasskeySetup — Register a passkey from within the Console
 *
 * Meant to be rendered inside AccountPanel or Profile settings.
 * Allows an already-authenticated user to register a passkey so
 * they can use biometric login on future visits.
 *
 * Uses Hanko Flow API `/profile` flow with handler-based pattern.
 */

import React, { useState } from 'react';
import hanko from '../hanko-init';

export default function PasskeySetup({ onComplete }) {
    const [status, setStatus] = useState('idle'); // idle | registering | success | error
    const [error, setError] = useState('');

    const handleRegister = async () => {
        setStatus('registering');
        setError('');

        let seenProfileInit = false;

        try {
            await hanko.flow.init('/profile', {
                onError: (err) => {
                    console.error('[PasskeySetup] Flow error:', err);
                    if (err.name === 'NotAllowedError') {
                        setError('Passkey registration was cancelled.');
                    } else {
                        setError(err.message || 'Failed to register passkey.');
                    }
                    setStatus('error');
                },
                preflight: (state) => {
                    // Register WebAuthn client capabilities
                    const webauthnAvailable = !!window.PublicKeyCredential;
                    let platform = false, conditional = false;
                    try { platform = webauthnAvailable && window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable && window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable(); } catch { }
                    try { conditional = webauthnAvailable && window.PublicKeyCredential.isConditionalMediationAvailable && window.PublicKeyCredential.isConditionalMediationAvailable(); } catch { }
                    return Promise.all([Promise.resolve(platform), Promise.resolve(conditional)]).then(([p, c]) =>
                        state.actions.register_client_capabilities({
                            webauthn_available: webauthnAvailable,
                            webauthn_platform_authenticator_available: p,
                            webauthn_conditional_mediation_available: c,
                        }).run()
                    );
                },
                profile_init: (state) => {
                    if (seenProfileInit) {
                        // Second time we hit profile_init → passkey was registered successfully
                        console.log('[PasskeySetup] Passkey registered successfully');
                        setStatus('success');
                        if (onComplete) setTimeout(() => onComplete(), 1500);
                        return; // Don't return an action — stop the flow
                    }
                    seenProfileInit = true;

                    if (state.actions?.webauthn_credential_create) {
                        console.log('[PasskeySetup] Triggering passkey creation...');
                        return state.actions.webauthn_credential_create().run();
                    } else {
                        setError('Passkey registration is not available right now.');
                        setStatus('error');
                    }
                },
                webauthn_credential_verification: (state) => {
                    // The SDK populates the attestation response automatically
                    if (state.actions?.webauthn_verify_attestation_response) {
                        console.log('[PasskeySetup] Verifying attestation...');
                        return state.actions.webauthn_verify_attestation_response().run();
                    }
                },
            });
        } catch (err) {
            console.error('[PasskeySetup] Registration error:', err);
            if (err.name === 'NotAllowedError') {
                setError('Passkey registration was cancelled.');
            } else {
                setError(err.message || 'Failed to register passkey.');
            }
            setStatus('error');
        }
    };

    if (status === 'success') {
        return (
            <div style={styles.container}>
                <div style={styles.successBanner}>
                    <span style={styles.successIcon}>✓</span>
                    <span>Passkey registered successfully! You can now sign in with biometrics.</span>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <span style={styles.keyIcon}>🔑</span>
                <div>
                    <h4 style={styles.title}>Passkey Authentication</h4>
                    <p style={styles.description}>
                        Register a passkey to sign in with Touch ID, Face ID, or your password manager.
                        No password needed.
                    </p>
                </div>
            </div>

            {error && <p style={styles.error}>{error}</p>}

            <button
                onClick={handleRegister}
                disabled={status === 'registering'}
                style={{
                    ...styles.button,
                    ...(status === 'registering' ? styles.buttonDisabled : {}),
                }}
            >
                {status === 'registering' ? 'Registering...' : 'Register Passkey'}
            </button>
        </div>
    );
}

const styles = {
    container: {
        padding: '16px 0',
    },
    header: {
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
        marginBottom: '16px',
    },
    keyIcon: {
        fontSize: '24px',
        marginTop: '2px',
    },
    title: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#3C4043',
        margin: '0 0 4px',
    },
    description: {
        fontSize: '13px',
        color: '#5F6368',
        margin: 0,
        lineHeight: '1.4',
    },
    button: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        padding: '10px 16px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#FFFFFF',
        backgroundColor: '#1A73E8',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'background-color 0.15s',
        fontFamily: 'inherit',
    },
    buttonDisabled: {
        backgroundColor: '#BDC1C6',
        cursor: 'not-allowed',
    },
    error: {
        color: '#F44336',
        fontSize: '13px',
        margin: '0 0 12px',
        padding: '8px 12px',
        backgroundColor: '#FFEBEE',
        borderRadius: '4px',
    },
    successBanner: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 16px',
        backgroundColor: '#E8F5E9',
        borderRadius: '6px',
        color: '#2E7D32',
        fontSize: '14px',
    },
    successIcon: {
        fontSize: '18px',
        fontWeight: '700',
    },
};
